import React from "react";
import { Link, useLocation } from "react-router-dom";
import { X } from "lucide-react";
import { cn } from "../utils";
import { shell, typography } from "../tokens";
import { isNavActive } from "./nav-config";
import { HeaderIconButton } from "./app-header";

const ITEM_BASE =
  "flex flex-col items-center justify-center gap-1 w-full py-3 px-1 relative transition-colors duration-150 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-400 focus:outline-none group";

export function NavRail({ children, footer, className }) {
  return (
    <div
      className={cn(
        "hidden md:relative md:flex md:flex-col h-full flex-shrink-0",
        shell.railWidth,
        shell.bg,
        "border-r",
        shell.border,
        className
      )}
    >
      <nav className="flex flex-col flex-1 min-h-0">
        <div className="flex-1 min-h-0 overflow-y-auto pb-16">{children}</div>
        {footer && (
          <div className={cn("absolute bottom-0 left-0 right-0 border-t", shell.border, shell.bg)}>
            {footer}
          </div>
        )}
      </nav>
    </div>
  );
}

export function NavRailItem({ to, label, icon: Icon, exact = false, extraPaths = [], onClick, linkState }) {
  const { pathname } = useLocation();
  const active = isNavActive(pathname, to, exact) || extraPaths.some((p) => pathname.startsWith(p));
  const destination = linkState ? { pathname: to, state: linkState } : to;

  return (
    <Link
      to={destination}
      onClick={onClick}
      className={cn(
        ITEM_BASE,
        active ? shell.activeBg : cn(shell.idleText, shell.hoverBg, "hover:text-gray-900")
      )}
    >
      {active && (
        <span className={cn("absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-8 rounded-r-full", shell.indicator)} />
      )}
      <Icon
        size={22}
        className={active ? shell.activeText : "text-gray-400 group-hover:text-gray-700"}
        aria-hidden="true"
      />
      <span
        className={cn(
          "leading-none tracking-tight text-center",
          active ? cn(shell.activeText, "font-semibold") : "text-gray-400 group-hover:text-gray-700"
        )}
        style={{ fontSize: "0.625rem" }}
      >
        {label}
      </span>
    </Link>
  );
}

export function MobileNavOverlay({ open, onClose }) {
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 bg-black/40 z-40 md:hidden"
      aria-hidden="true"
      onClick={onClose}
    />
  );
}

export function MobileNavDrawer({ open, onClose, title, children, className, closeLabel }) {
  return (
    <>
      <MobileNavOverlay open={open} onClose={onClose} />
      <div
        className={cn(
          "md:hidden fixed inset-y-0 left-0 z-50 w-64 flex flex-col shadow-xl transform transition-transform duration-300",
          shell.bg,
          open ? "translate-x-0" : "-translate-x-full",
          className
        )}
        aria-label={title}
      >
        <div className={cn("flex items-center justify-between h-14 px-4 border-b", shell.border)}>
          <span className="text-gray-900" style={{ ...typography.bodyBold, fontSize: "0.85rem" }}>{title}</span>
          {onClose && (
            <HeaderIconButton aria-label={closeLabel || "Close menu"} onClick={onClose}>
              <X className="w-4 h-4" />
            </HeaderIconButton>
          )}
        </div>
        <nav className="flex flex-col flex-1 overflow-y-auto py-1">{children}</nav>
      </div>
    </>
  );
}

export function MobileNavItem({ to, label, icon: Icon, exact = false, onClick, linkState, extraPaths = [] }) {
  const { pathname } = useLocation();
  const active = isNavActive(pathname, to, exact) || extraPaths.some((p) => pathname.startsWith(p));
  const destination = linkState ? { pathname: to, state: linkState } : to;

  return (
    <Link
      to={destination}
      onClick={onClick}
      className={cn(
        "flex items-center gap-3 px-5 py-3 transition-colors focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-400 focus:outline-none",
        active
          ? cn(shell.activeBg, shell.activeText, "font-medium")
          : cn("text-gray-600", shell.hoverBg, "hover:text-gray-900")
      )}
      style={{ fontSize: "0.85rem" }}
    >
      <Icon size={20} className={active ? shell.activeText : "text-gray-400"} aria-hidden="true" />
      <span>{label}</span>
    </Link>
  );
}
